import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type { CursorPageResponse } from "@/components/ui/data-table/composables/server-table";
import type { UsageRequest, UsageRequestItem } from "@/schemas/usage_requests.schema";
import type { Filter } from "@/types/backend-filter";
import { useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { api } from "@/services/api";
import { useProjectStore } from "@/stores/project.store";
import { toServerTableResponse } from "../components/ui/data-table/composables/cursor-pagination";

export type UsageRequestTableResponse = Pick<
  UsageRequest,
  "id" | "event_timestamp" | "agent_id" | "user_id" | "total_price_local" | "guest" | "api_key_id" | "auth_mode" | "source_service" | "project_id"
> & {
  items: Pick<
    UsageRequestItem,
    "charge_subtype" | "identifier" | "display_identifier" | "purchased_by_user_id" | "pricing_rule" | "quantity" | "unit"
  >[];
};

/**
 * Query keys factory for usage requests following Pinia Colada best practices
 * Includes projectId in keys for proper cache separation between projects
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const USAGE_REQUESTS_QUERY_KEYS = {
  root: ["usage-requests"] as const,
  list: (projectId: string | null | undefined, filters?: Filter) =>
    [...USAGE_REQUESTS_QUERY_KEYS.root, "list", { projectId }, filters] as const,
  details: (id: string) => [...USAGE_REQUESTS_QUERY_KEYS.root, "detail", id] as const,
};

/**
 * Query hook for fetching paginated usage requests
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Shared cache across components
 * - Background refetch on window focus
 * - Query refetches when selected project changes
 *
 * @param filters - Reactive LoopBack filter parameters
 *
 * @example
 * ```ts
 * const filters = computed(() => ({
 *   where: { user_id: userId.value },
 *   limit: 10,
 *   offset: 0,
 * }));
 *
 * const { data, isLoading, error, refetch } = useUsageRequestsQuery(filters);
 * ```
 */
export function useUsageRequestsQuery(
  filters: MaybeRefOrGetter<Filter>,
  options?: { globalView?: boolean },
) {
  const projectStore = useProjectStore();
  const effectiveProjectId = options?.globalView ? null : projectStore.currentProjectId;

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () => USAGE_REQUESTS_QUERY_KEYS.list(effectiveProjectId, toValue(filters)) as EntryKey,
    query: async () => {
      const filterValue = toValue(filters);
      const { offset: _offset, skip: _skip, ...apiFilter } = filterValue;
      const { data } = await api.get<CursorPageResponse<UsageRequestTableResponse>>(
        "/dashboard/usage-requests",
        {
          params: {
            filter: apiFilter,
          },
          headers: options?.globalView ? {} : projectStore.getProjectHeader(),
        }
      );

      return toServerTableResponse(data);
    },
    // Keep previous data while loading next page (prevents flicker)
    // Recommended for paginated queries - maintains visual continuity
    placeholderData: (previousData) => previousData,
    staleTime: 30_000, // 30 seconds - data is considered fresh
    gcTime: 5 * 60_000, // 5 minutes - garbage collection time
  });
}

/**
 * Query hook for fetching a single usage request by ID
 *
 * @param id - Usage request ID (can be reactive)
 *
 * @example
 * ```ts
 * const requestId = ref('abc-123');
 * const { data, isLoading } = useUsageRequestQuery(requestId);
 * ```
 */
export function useUsageRequestQuery(id: MaybeRefOrGetter<string>) {
  return useQuery({
    key: () => USAGE_REQUESTS_QUERY_KEYS.details(toValue(id)) as any,
    query: async () => {
      const idValue = toValue(id);

      const { data } = await api.get<UsageRequest>(`/usage-requests/${idValue}`);

      return data;
    },
    // Only run query if ID exists
    enabled: () => !!toValue(id),
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
  });
}

/**
 * Helper hook to invalidate usage request queries
 *
 * Use this after mutations that affect usage requests (create, update, delete)
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateUsageRequests();
 *
 * const { mutate } = useMutation({
 *   mutation: deleteUsageRequest,
 *   onSuccess: () => {
 *     invalidate(); // Refetch all usage request queries
 *   },
 * });
 * ```
 */
export function useInvalidateUsageRequests() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all usage request queries (both list and details)
     */
    all: () => queryCache.invalidateQueries({ key: USAGE_REQUESTS_QUERY_KEYS.root as any }),

    /**
     * Invalidate only list queries (preserves detail queries)
     */
    lists: () =>
      queryCache.invalidateQueries({ key: [...USAGE_REQUESTS_QUERY_KEYS.root, "list"] as any }),

    /**
     * Invalidate a specific usage request detail query
     */
    detail: (id: string) =>
      queryCache.invalidateQueries({ key: USAGE_REQUESTS_QUERY_KEYS.details(id) as any }),
  };
}
